import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { InviteUserDialog } from "@/components/team/InviteUserDialog";
import { apiClient } from "@/lib/api/client";
import { useSession } from "@/hooks/useSession";
import type { TeamMemberPayload, OrganizationInfoPayload } from "@/lib/api/types";
import * as React from "react";

const ROLE_LABELS: Record<string, string> = {
  owner: "Owner",
  admin: "Admin",
  member: "Member",
  viewer: "Viewer",
};

const ROLE_COLORS: Record<string, string> = {
  owner: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
  admin: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  member: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200",
  viewer: "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200",
};

export default function TeamPage() {
  const session = useSession();

  const [members, setMembers] = React.useState<TeamMemberPayload[]>([]);
  const [organization, setOrganization] = React.useState<OrganizationInfoPayload | null>(null);
  const [currentUserRole, setCurrentUserRole] = React.useState<string>("viewer");
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [actionLoading, setActionLoading] = React.useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = React.useState(false);
  const [inviteLoading, setInviteLoading] = React.useState(false);
  const [roleEditId, setRoleEditId] = React.useState<string | null>(null);
  const [roleEditValue, setRoleEditValue] = React.useState<string>("");

  const loadTeam = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiClient.getTeamMembers();
      setMembers(data.members);
      setOrganization(data.organization);
      setCurrentUserRole(data.currentUserRole);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load team.");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void loadTeam();
  }, [loadTeam]);

  const canManageMembers = currentUserRole === "owner" || currentUserRole === "admin";
  const canChangeRoles = currentUserRole === "owner";

  const handleInvite = async (email: string, role: string) => {
    setInviteLoading(true);
    try {
      await apiClient.inviteTeamMember({ email, role });
      await loadTeam();
    } finally {
      setInviteLoading(false);
    }
  };

  const handleRemove = async (memberId: string) => {
    const memberToRemove = members.find((member) => member.id === memberId);
    const displayName = memberToRemove?.name ?? memberToRemove?.email ?? "this member";
    const shouldRemove = window.confirm(`Remove ${displayName} from your organization? This action cannot be undone.`);
    if (!shouldRemove) {
      return;
    }

    setActionLoading(`remove:${memberId}`);
    setError(null);
    try {
      await apiClient.removeTeamMember(memberId);
      setMembers((prev) => prev.filter((m) => m.id !== memberId));
      if (organization) {
        setOrganization({ ...organization, memberCount: organization.memberCount - 1 });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to remove member.");
    } finally {
      setActionLoading(null);
    }
  };

  const handleRoleChange = async (memberId: string) => {
    if (!roleEditValue) return;
    setActionLoading(`role:${memberId}`);
    setError(null);
    try {
      const result = await apiClient.updateMemberRole(memberId, { role: roleEditValue });
      setMembers((prev) =>
        prev.map((m) => (m.id === memberId ? result.member : m)),
      );
      setRoleEditId(null);
      setRoleEditValue("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to update role.");
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Team</CardTitle>
            <CardDescription>Loading team members...</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <CardTitle>Team Members</CardTitle>
              <CardDescription>
                {organization
                  ? `${organization.name} \u00b7 ${organization.memberCount}/${organization.memberLimit === Number.MAX_SAFE_INTEGER ? "\u221e" : organization.memberLimit} members \u00b7 ${organization.subscriptionTier} tier`
                  : "Manage your team"}
              </CardDescription>
            </div>
            {canManageMembers ? (
              <Button onClick={() => setInviteOpen(true)}>
                Invite member
              </Button>
            ) : null}
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {error ? <p className="text-sm text-destructive">{error}</p> : null}

          {!error && members.length === 0 ? (
            <p className="text-sm text-muted-foreground">No team members found.</p>
          ) : null}

          <div className="space-y-3">
            {members.map((member) => {
              const isCurrentUser = member.userId === session.user?.id;
              const canRemoveThisMember =
                canManageMembers &&
                !isCurrentUser &&
                member.role !== "owner" &&
                (currentUserRole === "owner" || member.role !== "admin");
              const canChangeThisRole =
                canChangeRoles &&
                !isCurrentUser &&
                member.role !== "owner";
              const isEditing = roleEditId === member.id;

              return (
                <div
                  key={member.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-md border p-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate font-medium">
                        {member.name ?? member.email}
                      </span>
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${ROLE_COLORS[member.role] ?? ROLE_COLORS.viewer}`}
                      >
                        {ROLE_LABELS[member.role] ?? member.role}
                      </span>
                      {isCurrentUser ? (
                        <span className="text-xs text-muted-foreground">(you)</span>
                      ) : null}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {member.email}
                      {member.createdAt
                        ? ` \u00b7 Joined ${new Date(member.createdAt).toLocaleDateString()}`
                        : ""}
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {canChangeThisRole ? (
                      isEditing ? (
                        <div className="flex items-center gap-1">
                          <select
                            value={roleEditValue}
                            onChange={(e) => setRoleEditValue(e.target.value)}
                            className="h-8 rounded-md border border-input bg-transparent px-2 text-xs"
                          >
                            <option value="viewer">Viewer</option>
                            <option value="member">Member</option>
                            <option value="admin">Admin</option>
                          </select>
                          <Button
                            size="sm"
                            onClick={() => void handleRoleChange(member.id)}
                            disabled={actionLoading !== null || roleEditValue === member.role}
                          >
                            {actionLoading === `role:${member.id}` ? "Saving..." : "Save"}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => { setRoleEditId(null); setRoleEditValue(""); }}
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setRoleEditId(member.id);
                            setRoleEditValue(member.role);
                          }}
                          disabled={actionLoading !== null}
                        >
                          Change role
                        </Button>
                      )
                    ) : null}

                    {canRemoveThisMember ? (
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => void handleRemove(member.id)}
                        disabled={actionLoading !== null}
                      >
                        {actionLoading === `remove:${member.id}` ? "Removing..." : "Remove"}
                      </Button>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>

          {organization && organization.memberCount >= organization.memberLimit ? (
            <p className="text-sm text-yellow-600">
              Team member limit reached ({organization.memberCount}/{organization.memberLimit}).
              Upgrade your plan to add more members.
            </p>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Role permissions</CardTitle>
          <CardDescription>What each role can do in ShipPeek TMS</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="pb-2 pr-4 font-medium">Permission</th>
                  <th className="pb-2 pr-4 font-medium">Owner</th>
                  <th className="pb-2 pr-4 font-medium">Admin</th>
                  <th className="pb-2 pr-4 font-medium">Member</th>
                  <th className="pb-2 font-medium">Viewer</th>
                </tr>
              </thead>
              <tbody className="text-muted-foreground">
                <tr className="border-b">
                  <td className="py-2 pr-4">View quotes &amp; shipments</td>
                  <td className="py-2 pr-4">{"\u2713"}</td>
                  <td className="py-2 pr-4">{"\u2713"}</td>
                  <td className="py-2 pr-4">{"\u2713"}</td>
                  <td className="py-2">{"\u2713"}</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2 pr-4">Create quotes &amp; shipments</td>
                  <td className="py-2 pr-4">{"\u2713"}</td>
                  <td className="py-2 pr-4">{"\u2713"}</td>
                  <td className="py-2 pr-4">{"\u2713"}</td>
                  <td className="py-2">{"\u2014"}</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2 pr-4">Manage carriers &amp; templates</td>
                  <td className="py-2 pr-4">{"\u2713"}</td>
                  <td className="py-2 pr-4">{"\u2713"}</td>
                  <td className="py-2 pr-4">{"\u2713"}</td>
                  <td className="py-2">{"\u2014"}</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2 pr-4">Invite &amp; remove members</td>
                  <td className="py-2 pr-4">{"\u2713"}</td>
                  <td className="py-2 pr-4">{"\u2713"}</td>
                  <td className="py-2 pr-4">{"\u2014"}</td>
                  <td className="py-2">{"\u2014"}</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2 pr-4">Change member roles</td>
                  <td className="py-2 pr-4">{"\u2713"}</td>
                  <td className="py-2 pr-4">{"\u2014"}</td>
                  <td className="py-2 pr-4">{"\u2014"}</td>
                  <td className="py-2">{"\u2014"}</td>
                </tr>
                <tr>
                  <td className="py-2 pr-4">Billing &amp; subscription</td>
                  <td className="py-2 pr-4">{"\u2713"}</td>
                  <td className="py-2 pr-4">{"\u2713"}</td>
                  <td className="py-2 pr-4">{"\u2014"}</td>
                  <td className="py-2">{"\u2014"}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <InviteUserDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onInvite={handleInvite}
        loading={inviteLoading}
        currentUserRole={currentUserRole}
      />
    </div>
  );
}
